import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Space, Tabs } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { parseAsString, parseAsStringEnum, useQueryState, useQueryStates } from "nuqs";
import React, { useMemo, useState } from "react";

import { userSelectors } from "@fitness-app/app-store";
import { PermissionScope, TeamMemberRole } from "@fitness-app/data-models";
import { type TeamMember } from "@fitness-app/data-models/entities/TeamMember";

import { supabase, useAppSelector } from "~/store/initializeStore";
import { FILTER_DATE_FORMAT } from "./salesCrmConstants";
import { isSalesCrmTabKey, salesCrmTabKeys } from "./salesCrmTabs";
import { useSalesMeetingsQuery, type SalesMeetingsDateMode } from "./hooks/useSalesMeetingsQuery";
import { SalesAgent } from "./SalesAgent";
import SalesBlacklist, { type BlacklistPrefill } from "./SalesBlacklist";
import { SalesCallAnalysisTab } from "./SalesCallAnalysisTab";
import { SalesSettlements } from "./SalesSettlements";
import { SalesSmsTemplates } from "./SalesSmsTemplates";
import { SalesDashboardTab } from "./tabs/SalesDashboardTab";
import { SalesMeetingsTab } from "./tabs/SalesMeetingsTab";
import { SalesSellersTab } from "./tabs/SalesSellersTab";
import {
  createSalesCrmAccess,
  getSalesMeetingCapabilities,
  getSalesMeetingSellerId,
  resolveVisibleSalesCrmTab,
} from "./salesCrmAccess";

const SalesCRM = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const [{ from, to, seller, dateMode, clientEmail }, setUrlFilters] = useQueryStates(
    {
      from: parseAsString.withDefault(dayjs().startOf("month").format(FILTER_DATE_FORMAT)),
      to: parseAsString.withDefault(dayjs().format(FILTER_DATE_FORMAT)),
      seller: parseAsString,
      dateMode: parseAsStringEnum<SalesMeetingsDateMode>(["meeting", "sale"]).withDefault("meeting"),
      clientEmail: parseAsString.withDefault(""),
    },
    { history: "push", shallow: false, clearOnDefault: true },
  );
  const range = useMemo<[Dayjs, Dayjs]>(() => {
    const fromDate = dayjs(from);
    const toDate = dayjs(to);

    if (!fromDate.isValid() || !toDate.isValid() || fromDate.isAfter(toDate)) {
      return [dayjs().startOf("month"), dayjs().endOf("day")];
    }

    return [fromDate, toDate];
  }, [from, to]);
  const setRange = (value: [Dayjs, Dayjs]) => {
    void setUrlFilters({
      from: value[0].format(FILTER_DATE_FORMAT),
      to: value[1].format(FILTER_DATE_FORMAT),
    });
  };
  const selectedSellerId = seller;
  const setSelectedSellerId = (value: string | null) => {
    void setUrlFilters({ seller: value });
  };
  const [blacklistPrefill, setBlacklistPrefill] = useState<BlacklistPrefill | null>(null);
  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringEnum([...salesCrmTabKeys]).withDefault("dashboard"),
  );
  const permissions = useAppSelector(userSelectors.getUserPermissions);
  const { memberId, memberRole, trainerId } = useAppSelector(userSelectors.getUserClaims);
  const userId = useAppSelector((state) => state.user.data?.id);
  const selectedMember = useAppSelector((state) => state.team.selectedMember);
  const access = useMemo(
    () => createSalesCrmAccess(permissions, memberId, memberRole),
    [memberId, memberRole, permissions],
  );
  const scopedSellerId = getSalesMeetingSellerId(access, selectedSellerId);
  const canQueryMeetings =
    access.canReadSales && (access.salesReadScope !== PermissionScope.ASSIGNED || Boolean(scopedSellerId));

  const managedAssignmentsQuery = useQuery({
    queryKey: ["salesCrmManagedSellers", access.memberId],
    enabled: access.salesManageScope === PermissionScope.TEAM && Boolean(access.memberId),
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_member_assigned")
        .select("assignedTrainerId")
        .eq("headCoachId", access.memberId!);

      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
  const assignedManagedMemberIds = useMemo(
    () => new Set((managedAssignmentsQuery.data ?? []).map(({ assignedTrainerId }) => assignedTrainerId)),
    [managedAssignmentsQuery.data],
  );

  const sellersQuery = useQuery({
    queryKey: ["salesCrmSellers"],
    enabled:
      access.canSelectSalesSeller || access.canManageSales || access.canReadSettlements || access.canInspectAgentChats,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.from("team_member").select("*").order("firstName", { ascending: true });

      if (error) throw new Error(error.message);
      return ((data ?? []) as TeamMember[]).filter((member) => member.role === TeamMemberRole.SALES_REP);
    },
  });

  const meetingsQuery = useSalesMeetingsQuery({
    range,
    sellerId: scopedSellerId,
    enabled: canQueryMeetings,
  });

  const meetings = meetingsQuery.data?.list ?? [];
  const meetingsInitialLoading = meetingsQuery.isInitialLoading;
  const sellers = sellersQuery.data ?? [];
  const sellerOptions = sellers.map((seller) => ({
    value: seller.id,
    label: `${seller.firstName} ${seller.lastName}`,
  }));
  const getMeetingCapabilities = (targetMemberId: string | null) =>
    getSalesMeetingCapabilities(access, targetMemberId, assignedManagedMemberIds);
  const managedSellerOptions = sellerOptions.filter(({ value }) => getMeetingCapabilities(value).canUpdate);
  const lockedManagedSellerId = access.salesManageScope === PermissionScope.ASSIGNED ? access.memberId : null;
  const activeVisibleTab = resolveVisibleSalesCrmTab(activeTab, access) ?? "dashboard";
  const isAgentTab = activeVisibleTab === "agent";
  const isTabVisible = (key: string) => isSalesCrmTabKey(key) && access.visibleTabs.includes(key);
  // Without the seller list the settlement rows fall back to the default
  // commission config and would show made-up rates — a seller only ever needs
  // their own row, which redux already holds in full (TRE-1827).
  const settlementSellers = access.canReadSettlements ? sellers : selectedMember ? [selectedMember] : [];

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }} className={isAgentTab ? "h-full" : undefined}>
      {meetingsQuery.isError ? (
        <Alert
          showIcon
          type="warning"
          message="Sales CRM nie jest jeszcze gotowy w tej bazie"
          description={(meetingsQuery.error as Error).message}
        />
      ) : null}
      <Tabs
        activeKey={activeVisibleTab}
        onChange={(key) => void setActiveTab(isSalesCrmTabKey(key) && isTabVisible(key) ? key : activeVisibleTab)}
        className={
          isAgentTab
            ? "sales-crm-tabs flex min-h-[calc(100vh-140px)] flex-col [&_.ant-tabs-content-holder]:min-h-0 [&_.ant-tabs-content-holder]:flex-1 [&_.ant-tabs-content]:h-full [&_.ant-tabs-tabpane]:h-full [&_.ant-tabs-tabpane-active]:h-full"
            : "sales-crm-tabs"
        }
        items={[
          ...(isTabVisible("dashboard")
            ? [
                {
                  key: "dashboard",
                  label: "Dashboard",
                  children: (
                    <SalesDashboardTab
                      range={range}
                      setRange={setRange}
                      canSeeAll={access.canSelectSalesSeller}
                      sellerOptions={sellerOptions}
                      selectedSellerId={selectedSellerId}
                      setSelectedSellerId={setSelectedSellerId}
                      meetings={meetings}
                      loading={meetingsInitialLoading}
                      onSeeAllMeetings={() => void setActiveTab("meetings")}
                    />
                  ),
                },
              ]
            : []),
          ...(isTabVisible("meetings")
            ? [
                {
                  key: "meetings",
                  label: "Spotkania",
                  children: (
                    <SalesMeetingsTab
                      isActive={activeTab === "meetings"}
                      access={{
                        canManageMeetings: access.canManageSales,
                        canSelectSeller: access.canSelectSalesSeller,
                        canSyncMeetings: access.canManageSalesGlobally,
                        getMeetingCapabilities,
                        lockedManagedSellerId,
                      }}
                      range={range}
                      setRange={setRange}
                      dateMode={dateMode}
                      setDateMode={(value) => void setUrlFilters({ dateMode: value })}
                      clientEmail={clientEmail}
                      setClientEmail={(value) => void setUrlFilters({ clientEmail: value })}
                      sellerOptions={sellerOptions}
                      managedSellerOptions={managedSellerOptions}
                      selectedSellerId={selectedSellerId}
                      setSelectedSellerId={setSelectedSellerId}
                      scopedSellerId={scopedSellerId}
                      canQueryMeetings={canQueryMeetings}
                      selectedMember={selectedMember}
                      userId={userId}
                      onBlacklist={(prefill) => {
                        setBlacklistPrefill(prefill);
                        void setActiveTab("blacklist");
                      }}
                    />
                  ),
                },
              ]
            : []),
          ...(isTabVisible("agent")
            ? [
                {
                  key: "agent",
                  label: "Sales agent",
                  children: (
                    <SalesAgent
                      canInspectSalesRepChats={access.canInspectAgentChats}
                      canManageSkills={access.canManageAgentSkills}
                    />
                  ),
                },
              ]
            : []),
          ...(isTabVisible("templates")
            ? [
                {
                  key: "templates",
                  label: "Szablony SMS",
                  children: <SalesSmsTemplates />,
                },
              ]
            : []),
          ...(isTabVisible("blacklist")
            ? [
                {
                  key: "blacklist",
                  label: "Blacklista",
                  children: (
                    <SalesBlacklist prefill={blacklistPrefill} onPrefillConsumed={() => setBlacklistPrefill(null)} />
                  ),
                },
              ]
            : []),
          ...(isTabVisible("call-analysis")
            ? [
                {
                  key: "call-analysis",
                  label: "Analiza rozmów",
                  children: (
                    <SalesCallAnalysisTab
                      ownerId={trainerId ?? ""}
                      sellers={sellers.filter((seller) => getMeetingCapabilities(seller.id).canUpdate)}
                      meetings={meetings}
                      meetingsLoading={meetingsInitialLoading}
                    />
                  ),
                },
              ]
            : []),
          ...(isTabVisible("settlements")
            ? [
                {
                  key: "settlements",
                  label: "Rozliczenia",
                  children: (
                    <SalesSettlements
                      canManageConfiguration={access.canManageSettlementConfiguration}
                      isOwnRowOnly={!access.canReadSettlements}
                      sellers={settlementSellers}
                      onConfigSaved={() =>
                        void queryClient.invalidateQueries({
                          queryKey: ["salesCrmSellers"],
                        })
                      }
                    />
                  ),
                },
              ]
            : []),
          ...(isTabVisible("sellers")
            ? [
                {
                  key: "sellers",
                  label: "Sprzedawcy",
                  children: <SalesSellersTab sellers={sellers} loading={sellersQuery.isLoading} />,
                },
              ]
            : []),
        ]}
      />
    </Space>
  );
};

export default SalesCRM;
